/* eslint-disable @typescript-eslint/no-unused-vars */
import { observable, makeObservable, computed, action, reaction } from 'mobx';
import {
    ISizeType,
    ISalesArticleVariantSize,
    IAvailableColor,
    IBrickItem,
    VariantType,
    IModel,
    IColor,
    ISize,
    IAvailableModel,
    IBrickSelection,
    IVariantSelection,
    IResponseDataRefresh,
    IDeliveryInformation,
} from './types';
import { ScrollLock } from '../helpers/scrollLock';
import { MainStore } from './MainStore';
import defaultStyles from '../styles/default.scss';
import TextHelper from '../helpers/textHelper';
// import * as helper from '../helpers/html-helper';

export class VariantStore {
    public mainStore: MainStore;
    public readonly scrollLock = new ScrollLock();
    public orderModuleBrickSelection: IBrickSelection;
    public selectedBrickItem: IBrickItem;
    public sbiVariantSelection: IVariantSelection;
    private mobileMinWidth = parseInt(defaultStyles.breakpointMobileMinWidth);
    private vspTimer: ReturnType<typeof setTimeout>;

    // @observable:
    // UI
    public isVariantPanelOpen: boolean;
    public isVariantSelectionPanelOpen: boolean;
    public detailsModalOpen: boolean;
    public variantType: VariantType;
    public allowedInteraction: boolean;
    public panelPosition: { top: number; height: string } | null;
    public brickItemPosition: number; // for piwik

    constructor(mainStore: MainStore) {
        this.mainStore = mainStore;
        this.isVariantPanelOpen = false;
        this.isVariantSelectionPanelOpen = false;
        this.detailsModalOpen = false;
        this.panelPosition = null;
        this.variantType = VariantType.None;
        this.orderModuleBrickSelection = {} as IBrickSelection;
        this.selectedBrickItem = {} as IBrickItem;
        this.sbiVariantSelection = {} as IVariantSelection;
        this.allowedInteraction = true;
        this.brickItemPosition = 0;

        makeObservable(this, {
            isVariantPanelOpen: observable,
            isVariantSelectionPanelOpen: observable,
            brickItemPosition: observable,
            detailsModalOpen: observable,
            panelPosition: observable,
            variantType: observable,
            orderModuleBrickSelection: observable,
            selectedBrickItem: observable,
            sbiVariantSelection: observable,
            allowedInteraction: observable,
            getSortedBrickItems: computed,
            getOMBrickSelection: computed,
            getOMBrickPosition: computed,
            setBrickItemPosition: action,
            changeVariantSelectionPanelDisplay: action,
            changeVariantPanelDisplay: action,
            setDetailsModalOpen: action,
            updateSelectedBrickItem: action,
            selectedColor: computed,
            changeSelectedColor: action,
            changeSelectedSizeType: action,
            changeSelectedSize: action,
            changeSelectedModel: action,
            selectedSizeType: computed,
            selectedSize: computed,
            sideImagePath: computed,
            selectedModel: computed,
            resetVariantState: action,
            changeInteraction: action,
            showSizeAccuracy: computed,
            brickItemSelectionChanged: action,
            shouldChangeVariantSelectionType: computed,
            changeVariantSelectionType: action,
            openVariantSelectionPanel: action
        });

        this.openVariantSelectionPanel = this.openVariantSelectionPanel.bind(this);
        this.orderModuleBrickChanged = this.orderModuleBrickChanged.bind(this);

        reaction(() => this.isVariantPanelOpen, (value) => {
            if (value) {
                this.mainStore.hideEsAppButton();
            } else {
                this.mainStore.showEsAppButton();
            }
        });

        reaction(() => this.mainStore.orderModuleStore.selectedBrickNo,
            (value, previousValue, reaction) => this.orderModuleBrickChanged(value, previousValue, reaction));
    }

    openVariantSelectionPanel() {
        this.isVariantSelectionPanelOpen = true;
        this.changeVariantSelectionPanelDisplay(true, this.getVariantType(), 1024);
    }


    // handle selected brick change in order module:
    private orderModuleBrickChanged(value, previousValue, reaction) {
        // Do not trigger any further actions. 
        // Update ping came from color, size or brick item change event
        if (this.isVariantPanelOpen && value !== null
            && this.selectedBrickItem.brickNo === this.getOMBrickSelection.brickNo)
            return;

        if (value !== null && value !== previousValue) {
            this.orderModuleBrickSelection = this.getOMBrickSelection;

            const omSelectedBrick = this.getSortedBrickItems.find(b =>
                b.brickNo === this.orderModuleBrickSelection.brickNo
                && b.brickItemNo === this.orderModuleBrickSelection.selectedBrickItemNo);


            if (omSelectedBrick) {
                this.brickItemSelectionChanged(omSelectedBrick, false);
            }

            this.variantType = this.sbiVariantSelection?.showColorSwitcher
                ? VariantType.Color
                : VariantType.None;

            this.isVariantPanelOpen = true;

            // if there is only one brick item, open variant selection too:
            if (this.orderModuleBrickSelection.amountOfItems <= 1) {
                clearTimeout(this.vspTimer);
                if (
                    this.getSortedBrickItems[0].variantSelection.showColorSwitcher
                    || this.getSortedBrickItems[0].variantSelection.showModelSwitcher
                    || this.getSortedBrickItems[0].variantSelection.showSizeSwitcher
                )
                    this.vspTimer = setTimeout(() => {
                        this.openVariantSelectionPanel();
                    }, 0);
            }

            if (!this.scrollLock.isLocked) {
                this.scrollLock.lock();
            }
        } else {
            this.isVariantPanelOpen = false;
            this.isVariantSelectionPanelOpen = false;
            this.scrollLock.unlock();
        }
    }

    private getVariantType() {
        if (this.sbiVariantSelection?.showColorSwitcher) {
            return VariantType.Color;
        } else if (this.sbiVariantSelection.showSizeSwitcher && !this.sbiVariantSelection.isScrew) {
            return VariantType.Size;
        } else if (this.sbiVariantSelection.showSizeSwitcher && this.sbiVariantSelection.isScrew) {
            return VariantType.Special;
        } else if (this.sbiVariantSelection?.showModelSwitcher) {
            return VariantType.Model;
        } else {
            return VariantType.None;
        }
    }

    // event handler in brickItemsPanel:
    public brickItemSelectionChanged(brickItem: IBrickItem, openDMAfterLoad: boolean): void {
        // remove previous selection:
        this.getSelectedBrickItem.isBrickItemSelected = false;

        // check new brickItem selection:
        brickItem.isBrickItemSelected = true;

        this.selectedBrickItem = JSON.parse(JSON.stringify(this.getSelectedBrickItem));
        this.sbiVariantSelection = this.selectedBrickItem.variantSelection;
        if (this.isVariantSelectionPanelOpen) {
            if (this.sbiVariantSelection) {
                this.changeVariantSelectionType();
            }
            else {
                this.changeVariantSelectionPanelDisplay(false)
            }
        }

        // store primary data of selected brick item, it will be appended after full data refresh:
        this.variantChange(
            brickItem.selectedSavKey,
            openDMAfterLoad,
            false
        );
    }

    // Normal method:
    public getArticleSetItem() {
        const setItem = this.mainStore.dataStore.shutterModule.productInfo.description.sortedArticleSetItemDescriptions
            .find(item => item.masterArticleNo === this.selectedBrickItem.masterArticleNo);
        if (setItem) return setItem;
        else return this.selectedBrickItem.setItem;
    }

    public getUrlByAvailableColor(color: IAvailableColor): string {
        const salesArticleVariantKey = color.salesArticleVariantKey.split('-');
        return this.getUrl(salesArticleVariantKey[0], salesArticleVariantKey[1]);
    }

    public anySizeExistForSizeType(sizeType: ISizeType): boolean {
        const existSize = sizeType.sizes.find((size: ISalesArticleVariantSize) => {
            return !!size.salesArticleVariantKey;
        });
        return !!existSize;
    }

    // @computed:
    get getOMBrickSelection() {
        const result = this.mainStore.dataStore.orderModule.sortedBrickSelections.filter(
            it => it.brickNo === this.mainStore.orderModuleStore.selectedBrickNo);

        return result[0];
    }

    // for piwik:
    get getOMBrickPosition() {
        return this.mainStore.orderModuleStore.selectedBrickPosition;
    }

    get getSortedBrickItems() {
        return this.mainStore.dataStore.sortedBrickItems.filter(it =>
            it.brickNo === this.orderModuleBrickSelection.brickNo
        );
    }

    private get getSelectedBrickItem() {
        const selectedItem = this.getSortedBrickItems.filter(it => it.isBrickItemSelected);

        return selectedItem[0];
    }

    private getUrl(salesArticleNo: string, colorCode: string): string {
        // this.mainStore.dataStore.contextData.originalUrl needed only for startup,
        // when server side rendering is in progress and window object is not yet defined.
        // During that moment it is the only source of truth and it is correct
        const rawUrl = typeof window !== 'undefined' ? window.location.href
            : this.mainStore.dataStore.contextData.originalUrl;
        const originalUrl = new URL(rawUrl);
        const lastItem = originalUrl.pathname.substring(originalUrl.pathname.lastIndexOf('/') + 1);
        const subStrings = lastItem.split('-');
        const colorCodeIndex = subStrings.length - 1;
        const salesArticleNoIndex = subStrings.length - 2;
        subStrings[colorCodeIndex] = colorCode;
        subStrings[salesArticleNoIndex] = salesArticleNo;
        const newLastPath = subStrings.join('-') + '.html';
        const url = new URL(newLastPath, originalUrl);
        const query = new URLSearchParams(originalUrl.search);
        query.forEach((value, key) => {
            url.searchParams.set(key, value);
        });
        return url.href;
    }

    private changeVariantType(variantType?: VariantType) {
        if (typeof variantType !== 'undefined') {
            if (variantType !== VariantType.Kiosk) {
                this.variantType = variantType;
            } else {
                if (this.sbiVariantSelection.showSizeSwitcher) {
                    if (this.sbiVariantSelection.isScrew) {
                        this.variantType = VariantType.Special;
                    } else {
                        this.variantType = VariantType.Size;
                    }
                } else if (this.sbiVariantSelection.showModelSwitcher) {
                    this.variantType = VariantType.Model;
                } else if (this.sbiVariantSelection.showColorSwitcher) {
                    this.variantType = VariantType.Color;
                }
            }
        }
    }

    // @action:
    changeVariantPanelDisplay(open: boolean, width?: number): void {
        // this.brickSelection = this.getBrickSelection;
        // this.selectedBrickItem =  JSON.parse(JSON.stringify(this.getSelectedBrickItem));
        // this.variantType = this.selectedBrickItem.variantSelection.showColorSwitcher
        // ? VariantType.Color
        // : VariantType.None;

        this.isVariantPanelOpen = open;
        this.isVariantSelectionPanelOpen = open;

        if (!open) {
            this.mainStore.orderModuleStore.setSelectedBrickNo(null);
        }
    }

    changeVariantSelectionPanelDisplay(
        open: boolean,
        variantType?: VariantType,
        width?: number,
        isFromVariantButton?: boolean): void {

        this.changeVariantType(variantType);

        // don't open panel when don't have proper type setup
        if (this.variantType === VariantType.None || this.variantType === VariantType.Kiosk)
            open = false;

        if (!this.isVariantSelectionPanelOpen || !open)
            if (open) {
                this.scrollLock.lock();

                // set panel position:
                if (width)
                    this.panelPosition = { top: 0, height: 'calc(100%)' };

            }

        this.isVariantSelectionPanelOpen = open;
        // if closing variant selection panel and there's only one item in brick
        if (!open && this.getSortedBrickItems.length <= 1) {
            // ... and it's not from a variant button click 
            // or it's on smaller devices that don't show both panels at the same time
            if (!isFromVariantButton || window.innerWidth <= 1023) {
                this.changeVariantPanelDisplay(false);
            }
        }
    }

    setDetailsModalOpen(value: boolean): void {
        this.detailsModalOpen = value;
    }

    // for piwik
    setBrickItemPosition(index: number): void {
        this.brickItemPosition = index;
    }

    changeSelectedColor(updatedSelectedColor: IColor): void {
        if (this.selectedBrickItem.selectedColor?.code !== updatedSelectedColor.code) {
            const selectedAvailableColor = this.sbiVariantSelection.availableColors.find(color => {
                return color.code === updatedSelectedColor.code;
            });

            if (selectedAvailableColor) {
                this.selectedBrickItem.selectedColor = updatedSelectedColor;
                if (this.mainStore.showKioskLayout) {
                    // not for now
                    // const tmp = this.mainStore.workWearStoreStore.variantAvailabilityState(updatedSelectedColor.code,
                    //     VariantType.Color,
                    //     selectedAvailableColor.salesArticleVariantKey,
                    //     this.mainStore.workWearStoreStore.variantInfoInKiosk);
                    // availabilityState = AvailabilityStatusEnum[tmp];
                }
                this.variantChange(
                    selectedAvailableColor.salesArticleVariantKey
                );

                // not for now
                // if (this.mainStore.showKioskLayout)
                //     this.mainStore.workWearStoreStore.changeSelectedColor(updatedSelectedColor);
            }
        }
    }

    changeSelectedModel(updatedSelectedModel: IModel): void {
        if (this.selectedBrickItem.selectedModel?.code !== updatedSelectedModel.code) {
            this.selectedBrickItem.selectedModel = updatedSelectedModel;
            this.updateModel();
        }
    }

    changeSelectedSizeType(updatedSelectedSizeType: string): void {
        const sizeType = this.sbiVariantSelection.availableSizeTypes.find(aSizeType => {
            return aSizeType.sizeTypeName === updatedSelectedSizeType;
        });
        if (sizeType) {
            this.selectedBrickItem.selectedSizeType = updatedSelectedSizeType;
            const defaultSize = sizeType.sizes.find(size => {
                return size.salesArticleVariantKey;
            });
            if (defaultSize)
                this.changeSelectedSize(defaultSize.size);
        }
    }

    changeSelectedSize(updatedSelectedSize: ISize): void {
        if (this.selectedBrickItem.selectedSize?.code !== updatedSelectedSize.code) {
            this.selectedBrickItem.selectedSize = updatedSelectedSize;
            this.updateVariantSizeSelected(this.isDeskTopView());
        }
    }

    resetVariantState() {
        this.selectedBrickItem = JSON.parse(JSON.stringify(this.getSelectedBrickItem));
    }

    private updateVariantSizeSelected(shouldUpdateWholePage: boolean) {
        if (this.selectedSize) {
            if (this.mainStore.showKioskLayout) {
                // not for now
                // if (this.selectedSize?.size.code) {
                //     const tmp = this.mainStore.workWearStoreStore.variantAvailabilityState(
                //         this.selectedSize?.size.code,
                //         VariantType.Model,
                //         this.selectedSize?.salesArticleVariantKey,
                //         this.mainStore.workWearStoreStore.variantInfoInKiosk);
                //     availabilityState = AvailabilityStatusEnum[tmp];
                // }
            }
            this.variantChange(
                this.selectedSize.salesArticleVariantKey
            );
        }
    }

    private updateModel() {
        const selectedAvailableModel = this.sbiVariantSelection.availableModels.find(model => {
            return model.code === this.selectedModel?.code;
        });
        if (selectedAvailableModel) {

            // not for now
            // if (this.mainStore.showKioskLayout) {
            //     const tmp = this.mainStore.workWearStoreStore.variantAvailabilityState(selectedAvailableModel.code,
            //         VariantType.Model,
            //         selectedAvailableModel.salesArticleVariantKey,
            //         this.mainStore.workWearStoreStore.variantInfoInKiosk);
            //     availabilityState = AvailabilityStatusEnum[tmp];
            // }
            this.variantChange(selectedAvailableModel.salesArticleVariantKey);
        }
    }

    public isDeskTopView(): boolean {
        return window.innerWidth > this.mobileMinWidth;
    }

    public getColumnsPerView(): number {
        if (this.isDeskTopView())
            return 5; // default value for desktop devices

        if (window.innerWidth <= parseInt(defaultStyles.breakpointMobileIphone12ProMax))
            return 5; // for mobile view
        else
            return 7; // for tablet view
    }

    public changeInteraction(allowedInteraction: boolean) {
        this.allowedInteraction = allowedInteraction;
    }

    public updateSelectedBrickItem(data: IResponseDataRefresh) {
        Object.assign(this.selectedBrickItem, data.brickItem);
        this.selectedBrickItem.setItem = data.articleSetItemDescription
        this.sbiVariantSelection = this.selectedBrickItem.variantSelection;

        if (this.isVariantSelectionPanelOpen) {
            if (this.sbiVariantSelection) {
                if (this.shouldChangeVariantSelectionType) {
                    this.changeVariantSelectionType();
                }
            }
            else {
                this.changeVariantSelectionPanelDisplay(false)
            }
        }
    }


    private taskId: string = '';
    private taskCount: number = 0;

    public variantChange(salesArticleVariantKey: string, openDMAfterLoad: boolean = false, trackMeta: boolean = true) {

        // check originalBrick:
        // const selectedItemFromOrderModal = this.getSortedBrickItems.filter(
        //     it => it.brickItemNo === this.mainStore.orderModuleStore.selectedBrickNo)[0];

        // if (this.mainStore.orderModuleStore.selectedBrickNo === this.selectedBrickItem.brickItemNo
        //     && selectedItemFromOrderModal.selectedColor?.code.toString() === colorCode
        //     && selectedItemFromOrderModal.selectedSize?.code.toString() === sizeCode) {
        //     return;
        // }


        const masterArticleNo = this.selectedBrickItem.masterArticleNo;

        this.changeInteraction(false);

        // Generate an ID that is used to track what the current request is
        const newTaskId = `${(this.taskCount++) % 1000}-${Date.now()}`;
        this.taskId = newTaskId;
        this.mainStore.fetch.dataRefresh(this.selectedBrickItem.fsCode, masterArticleNo, salesArticleVariantKey,
            this.selectedBrickItem.brickNo, this.selectedBrickItem.brickItemNo,
            this.getOtherSelected(this.selectedBrickItem.brickNo)
        ).then(response => {
            if (response) {
                if (this.isVariantPanelOpen && this.taskId === newTaskId) {
                    this.updateSelectedBrickItem(response);
                    this.mainStore.variantChanged(response);

                    if (openDMAfterLoad)
                        this.setDetailsModalOpen(true);
                    else this.resetVariantState();
                }
            }
            this.changeInteraction(true);
        });
    }

    private getOtherSelected(changedBrickNo: number): {
        masterArticleNo: number; salesArticleVariantKey: string;
        brickItemNo: number; brickNo: number;
        deliveryInformation: IDeliveryInformation; availabilityState: number;
    }[] {

        const otherSelected: {
            masterArticleNo: number; salesArticleVariantKey: string;
            brickItemNo: number; brickNo: number;
            deliveryInformation: IDeliveryInformation; availabilityState: number;
        }[] = [];

        this.mainStore.dataStore.sortedBrickItems.forEach((sbi) => {
            if (changedBrickNo != sbi.brickNo // changed brick item should be excluded from other selected
                && sbi.isBrickItemSelected) {
                otherSelected.push({
                    masterArticleNo: sbi.masterArticleNo,
                    salesArticleVariantKey: sbi.selectedSavKey,
                    brickItemNo: sbi.brickItemNo,
                    brickNo: sbi.brickNo,
                    deliveryInformation: sbi.deliveryInformation,
                    availabilityState: sbi.availabilityState
                });
            }
        });

        return otherSelected;
    }

    changeVariantSelectionType() {
        const tmpVariantType = this.getVariantType();

        const brickItems = this.getSortedBrickItems;
        const isSingleItemBrickNoVariants = this.getSortedBrickItems.length === 1 && !(
            brickItems[0].variantSelection.showColorSwitcher
            || brickItems[0].variantSelection.showSizeSwitcher
            || brickItems[0].variantSelection.showModelSwitcher
        )

        if (tmpVariantType === VariantType.None)
            this.changeVariantSelectionPanelDisplay(false, undefined, undefined, isSingleItemBrickNoVariants);
        else
            this.changeVariantType(tmpVariantType);
    }

    get selectedColor(): IColor {
        return {
            code: this.selectedBrickItem.selectedColor?.code ?? 0,
            name: TextHelper.addWhitespacesAroundSlashes(this.selectedBrickItem.selectedColor?.name)
        };
    }

    get selectedModel(): IModel | undefined {
        return this.selectedBrickItem.selectedModel;
    }

    get selectedSizeType(): ISizeType | undefined {
        const updatedSelectedSizeType = this.selectedBrickItem.selectedSizeType;
        return this.sbiVariantSelection.availableSizeTypes?.find(sizeType => {
            return sizeType.sizeTypeName === updatedSelectedSizeType;
        });
    }

    get selectedSize(): ISalesArticleVariantSize | undefined {
        let result: ISalesArticleVariantSize | undefined;
        const preselectedSizeType = this.selectedSizeType?.sizes?.find(size => {
            return size.size.code === this.selectedBrickItem.selectedSize?.code;
        });

        if (preselectedSizeType) result = preselectedSizeType;
        else result = this.selectedSizeType?.sizes[0];

        if (result?.size.fittingAccuracy)
            if (result.size.fittingAccuracy < -0.5) result.size.fittingAccuracy = -0.5;
            else if (result.size.fittingAccuracy > 0.5) result.size.fittingAccuracy = 0.5;

        return result;
    }

    get sideImagePath(): string {
        if (this.selectedModel && this.selectedModel.code != 0) {
            const selectedModel: IAvailableModel | undefined =
                this.sbiVariantSelection?.availableModels.find(model => {
                    return model.code === this.selectedModel?.code;
                });
            return selectedModel ? selectedModel.defaultArticleImagePath : '';
        } else if (this.selectedColor.code != 0) {
            const selectedColor: IAvailableColor | undefined =
                this.sbiVariantSelection?.availableColors.find(color => {
                    return color.code === this.selectedColor.code;
                });
            return selectedColor ? selectedColor.defaultArticleImagePath : '';
        }
        return '';
    }

    get variantButtonCount(): number {
        let variantButtonCount = 0;

        if (!this.sbiVariantSelection) {
            return variantButtonCount;
        }

        if (this.sbiVariantSelection.showColorSwitcher) {
            variantButtonCount++;
        }
        if (this.sbiVariantSelection.showSizeSwitcher) {
            variantButtonCount++;
        }
        if (this.sbiVariantSelection.showModelSwitcher) {
            variantButtonCount++;
        }
        return variantButtonCount;
    }

    // get showAvailableStatusInKiosk(): boolean {
    //     return !this.variantSelectionCopy.showModelSwitcher && !this.variantSelectionCopy.showSizeSwitcher;
    // }

    get showSizeAccuracy(): boolean {
        // Fitting Accuracy can be 0 and its a valid data to present
        return (this.selectedSize?.size.fittingAccuracy !== null) &&
            (this.selectedSize?.size.fittingAccuracy !== undefined);
    }

    get shouldChangeVariantSelectionType() {
        if (this.variantType === VariantType.Color && this.sbiVariantSelection.showColorSwitcher) {
            return false;
        }
        if (!this.sbiVariantSelection.isScrew && this.variantType === VariantType.Size
            && this.sbiVariantSelection.showSizeSwitcher) {
            return false;
        }
        if (this.sbiVariantSelection.isScrew && this.variantType === VariantType.Special
            && this.sbiVariantSelection.showSizeSwitcher) {
            return false;
        }
        if (this.variantType === VariantType.Model && this.sbiVariantSelection.showModelSwitcher) {
            return false;
        }
        return true
    }
}
